import {
  Paper,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableFooter,
  TableContainer,
  TablePagination,
} from '@mui/material';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { GenericTableHead, GenericTableRow } from '../../../employees/components/GenericTable/GenericTable.styled';
import type { JsonApiMetaI } from 'types';

export interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
}

interface RowWithId {
  id: string;
  [key: string]: unknown;
}

export interface ServerPaginatedTableProps<T extends RowWithId> {
  columns: Column[];
  rows: T[];
  meta?: JsonApiMetaI | null;
  loading?: boolean;
  error?: string | null;
  onRowClick?: (row: T) => void;
  getRowContent?: (row: T, columnId: string) => React.ReactNode;
  noDataMessage?: string;
  errorComponent?: React.ReactNode;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}

function ServerPaginatedTable<T extends RowWithId>({
  columns,
  rows = [],
  meta,
  error,
  onRowClick,
  getRowContent,
  noDataMessage = 'No hay datos disponibles',
  errorComponent = null,
  onPageChange,
  onRowsPerPageChange,
}: ServerPaginatedTableProps<T>) {
  const currentPage = (meta?.currentPage ?? 1) - 1;
  const rowsPerPage = meta?.itemsPerPage ?? 10;
  const totalItems = meta?.totalItems ?? rows.length;

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    onPageChange?.(newPage + 1); // API usa 1-indexed
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange?.(parseInt(event.target.value, 10));
  };

  if (error) {
    return errorComponent ? (
      errorComponent
    ) : (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" component="div">
          {error}
        </Typography>
      </Paper>
    );
  }

  if (rows.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" component="div">
          {noDataMessage}
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent' }}>
      <Table
        aria-label="server paginated table"
        sx={{ minWidth: 650, borderSpacing: '0px 5px', borderCollapse: 'separate' }}
      >
        <GenericTableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.id} align={column.align} sx={{ minWidth: column.minWidth }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </GenericTableHead>
        <TableBody>
          {rows.map(row => (
            <GenericTableRow key={row.id} isClickable={!!onRowClick} onClick={() => onRowClick?.(row)} sx={{ height: 64 }}>
              {columns.map(column => (
                <TableCell key={column.id} align={column.align} sx={{ whiteSpace: 'nowrap', fontSize: 14 }}>
                  {getRowContent ? getRowContent(row, column.id) : (row[column.id] as React.ReactNode)}
                </TableCell>
              ))}
            </GenericTableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={columns.length} align="right">
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 50, 100]}
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

export default ServerPaginatedTable;
